import { useEffect, useState } from 'react'

/**
 * True while the page is scrolling down past `offset`, false as soon as it scrolls back up, so a
 * phone's top bar can slide away while reading and return the moment the reader heads back.
 * Small jitters (under `threshold` px) are ignored so momentum scrolling doesn't flicker it.
 */
export function useHideOnScroll({
  enabled = true,
  offset = 72,
  threshold = 8,
}: { enabled?: boolean; offset?: number; threshold?: number } = {}): boolean {
  const [hidden, setHidden] = useState(false)

  useEffect(() => {
    if (!enabled) return
    let last = window.scrollY
    let frame = 0
    const onScroll = () => {
      if (frame) return
      frame = requestAnimationFrame(() => {
        frame = 0
        const y = window.scrollY
        const delta = y - last
        if (y <= offset) setHidden(false)
        else if (Math.abs(delta) >= threshold) setHidden(delta > 0)
        if (Math.abs(delta) >= threshold || y <= offset) last = y
      })
    }
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => {
      window.removeEventListener('scroll', onScroll)
      if (frame) cancelAnimationFrame(frame)
    }
  }, [enabled, offset, threshold])

  return enabled && hidden
}
